import {
  EventOccurrenceCreateVariables,
  EventOccurrenceEditVariables,
  useEventOccurrence,
  useEventOccurrenceCreate,
  useEventOccurrenceEdit
} from "@gymflow/api";
import {
  DATE_TIME_FORMAT,
  PARAMETER_DATE_FORMAT_WITHOUT_TZ,
} from "@gymflow/common";
import { EventOccurrenceDTO } from "@gymflow/types";
import { isAxiosError } from "axios";
import { NO_AVAILABLE_SLOTS_ERROR_CODES } from "libs/helpers/src/constants/ManuallyHandledErrorCodes";
import moment from "moment-timezone";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import { ModalContext, useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { NotAvailableModal } from "../NotAvailableModal/NotAvailableModal";
import { RecurrenceEditModal } from "./RecurrenceEditModal";
import { SideBarOccurrenceForm } from "./SideBarOccurrenceForm";

type MutationWithFallbackParams<TParams, TResult> = {
  mutationFn: (params: TParams) => Promise<TResult>;
  params: TParams;
};

interface SideBarOccurrenceFormProviderProps {
  children: ReactNode;
}

interface SideBarOccurrenceFormProviderContextType {
  openEdit: (occurrenceId: number) => void;
  openNew: (
    referenceDate: string,
    blankTime?: boolean,
    defaultHostId?: string | null,
    defaultFacilityId?: number | null,
  ) => void;
}

export const SideBarOccurrenceFormProviderContext =
  createContext<SideBarOccurrenceFormProviderContextType>({} as any);

export function SideBarOccurrenceFormProvider({
  children,
}: SideBarOccurrenceFormProviderProps) {
  const { club } = useClubSettings();
  const { api } = useGymflowModels();

  const [isVisible, setIsVisible] = useState(false);
  const [occurrenceId, setOccurrenceId] = useState<number | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | null>(null);
  const [defaultHostId, setDefaultHostId] = useState<string | null>(null);
  const [defaultFacilityId, setDefaultFacilityId] = useState<number | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isTimeBlanked, setIsTimeBlanked] = useState(false);

  const { data: existingOccurrence, isFetching } = useEventOccurrence({
    api,
    eventId: occurrenceId,
    timezone: club.timezone,
  });

  const createOccurrenceMutation = useEventOccurrenceCreate({
    api,
    tz: club?.timezone as string,
  });
  const editOccurrenceMutation = useEventOccurrenceEdit({
    api,
    tz: club?.timezone as string,
  });

  const modal = useContext(ModalContext);

  const mutationWithFallback = useCallback(
    async <TParams, TResult>({
      mutationFn,
      params,
    }: MutationWithFallbackParams<TParams, TResult>): Promise<TResult> => {
      try {
        const response = await mutationFn(params);
        setIsSaving(false);
        setIsVisible(false);
        return response;
      } catch (e) {
        if (
          isAxiosError(e) &&
          NO_AVAILABLE_SLOTS_ERROR_CODES.includes(e.response?.data.error_code)
        ) {
          return new Promise<TResult>((resolve, reject) => {
            modal.setModal(
              <NotAvailableModal
                errorCode={e.response?.data.error_code}
                onConfirm={async () => {
                  try {
                    const fallbackResponse = await mutationFn({
                      ...params,
                      ignoreAvailabilityValidation: true,
                    });
                    setIsSaving(false);
                    setIsVisible(false);
                    resolve(fallbackResponse);
                    modal.hide();
                  } catch (fallbackError) {
                    setIsSaving(false);
                    setIsVisible(false);
                    modal.hide();
                    reject(fallbackError);
                  }
                }}
                onCancel={() => {
                  setIsSaving(false);
                  modal.hide();
                  reject();
                }}
              />,
            );
          });
        } else {
          setIsSaving(false);
          setIsVisible(false);
          throw e;
        }
      }
    },
    [modal],
  );

  return (
    <SideBarOccurrenceFormProviderContext.Provider
      value={{
        openEdit: (occurrenceId) => {
          setIsTimeBlanked(false);
          setIsVisible(true);
          setOccurrenceId(occurrenceId);
        },
        openNew: (
          referenceDate,
          blankTime = false,
          defaultHostId = null,
          defaultFacilityId = null,
        ) => {
          setIsTimeBlanked(blankTime);
          setDefaultDate(referenceDate);
          setIsVisible(true);
          setDefaultHostId(defaultHostId);
          setDefaultFacilityId(defaultFacilityId);
        },
      }}
    >
      <SideBarOccurrenceForm
        isOpen={isVisible}
        onClose={() => {
          setIsVisible(false);
          setOccurrenceId(null);
          setDefaultHostId(null);
        }}
        defaultStartDate={
          existingOccurrence
            ? moment(
                existingOccurrence.startDate,
                PARAMETER_DATE_FORMAT_WITHOUT_TZ,
              ).format(DATE_TIME_FORMAT)
            : defaultDate
        }
        defaultHostId={defaultHostId}
        defaultFacilityId={defaultFacilityId}
        value={existingOccurrence as EventOccurrenceDTO | null}
        blankTime={isTimeBlanked}
        isLoading={isFetching || isSaving}
        // Refactor it; More info in the SideBarOccurrenceForm;
        // Maybe move this logic to the SideBarOccurrenceForm component.
        onChange={async (newValues) => {
          setIsSaving(true);
          let newOccurrenceId;
          if (occurrenceId) {
            if (existingOccurrence?.event.isRecurring) {
              modal.setModal(
                <RecurrenceEditModal
                  onConfirm={async (updateAll) => {
                    modal.hide();
                    await mutationWithFallback({
                      mutationFn: editOccurrenceMutation.mutateAsync,
                      params: {
                        eventId: occurrenceId,
                        patchedFields: {
                          ...newValues,
                        },
                        isUpdateAll: updateAll,
                      },
                    })
                      .then(() => {
                        // nothing
                      })
                      .catch(() => {
                        // nothing
                      });
                  }}
                  onCancel={() => {
                    setIsSaving(false);
                    modal.hide();
                  }}
                />,
              );
              return;
            } else if (newValues?.["isRecurring"]) {
              await mutationWithFallback<
                EventOccurrenceEditVariables,
                EventOccurrenceDTO
              >({
                mutationFn: editOccurrenceMutation.mutateAsync,
                params: {
                  eventId: occurrenceId,
                  patchedFields: newValues,
                  isUpdateAll: true,
                },
              })
                .then(({ id }) => {
                  newOccurrenceId = id;
                })
                .catch(() => {
                  // nothing
                });
            } else {
              await mutationWithFallback<
                EventOccurrenceEditVariables,
                EventOccurrenceDTO
              >({
                mutationFn: editOccurrenceMutation.mutateAsync,
                params: {
                  eventId: occurrenceId,
                  patchedFields: newValues,
                },
              })
                .then(({ id }) => {
                  newOccurrenceId = id;
                })
                .catch(() => {
                  // nothing
                });
            }
          } else {
            await mutationWithFallback<
              EventOccurrenceCreateVariables,
              EventOccurrenceDTO
            >({
              mutationFn: createOccurrenceMutation.mutateAsync,
              params: {
                fields: newValues,
              },
            })
              .then(({ id }) => {
                newOccurrenceId = id;
              })
              .catch(() => {
                // nothing
              });
          }
          // TODO Review this newOccurrenceId
          console.info(newOccurrenceId);
        }}
      />
      {children}
    </SideBarOccurrenceFormProviderContext.Provider>
  );
}
